import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Settings } from "../../../interfaces";
import Form from "react-bootstrap/Form";
import { TIERS } from "./variables";
import {
  useIonToast,
  IonList,
  IonSelectOption,
  IonItem,
  IonSelect,
  IonNote,
} from "@ionic/react";
import PlanUsage from "../plan/index";
import { IconCreditCardFilled, IconMail } from "@tabler/icons-react";
import Subscription from "../../../api/subscription";

const formatLocalDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

interface ILayoutProps {
  settings: Settings;
  showCta?: boolean;
  isMobile?: boolean;
}
const PlanManagement: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const [selected, setSelected] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [isLegacy, setIsLegacy] = React.useState(false);
  const [pendingDowngradeDate, setPendingDowngradeDate] = React.useState("");
  const [pendingDowngradePriceState, setPendingDowngradePriceState] =
    React.useState("");
  const [present] = useIonToast();
  const { isMobile } = props;
  const { payment, plan } = props.settings.status;
  const intl = useIntl();
  const { subscription, price, active, pendingDowngradePrice } =
    props.settings.status.payment;

  React.useEffect(() => {
    let findTier = null;
    if (active && price) {
      findTier = TIERS.find((t) => t.priceId === price);
      if (findTier) {
        setSelected(findTier.priceId);
      }
    }
    if (!findTier && active) {
      setIsLegacy(true);
    } else {
      setIsLegacy(false);
    }
  }, [price, active]);

  React.useEffect(() => {
    setPendingDowngradeDate(formatLocalDate(payment?.pendingDowngradeAt));
    setPendingDowngradePriceState(payment?.pendingDowngradePrice || "");
  }, [payment?.pendingDowngradeAt, payment?.pendingDowngradePrice]);

  const effectivePendingDowngradePrice =
    pendingDowngradePriceState || pendingDowngradePrice;
  const selectedTier = TIERS.find((t) => t.priceId === selected);
  const currentTier = TIERS.find((t) => t.priceId === price);
  const scheduledTier = TIERS.find(
    (t) => t.priceId === effectivePendingDowngradePrice,
  );
  const isUpgradeSelection =
    !!selectedTier &&
    !!currentTier &&
    selectedTier.priceUsd > currentTier.priceUsd;
  const changePlanCtaId = isUpgradeSelection
    ? "payment.changePlanPayNow"
    : "payment.changePlan";
  const currentPeriodEndDate = formatLocalDate(payment?.endDate);
  const currentPlanLabel = currentTier
    ? intl.formatMessage(
        {
          id: "payment.planTier",
        },
        {
          credits: currentTier?.credits,
          price: currentTier?.priceUsd,
        },
      )
    : plan?.name || "-";
  const scheduledPlanLabel = scheduledTier
    ? intl.formatMessage(
        {
          id: "payment.planTier",
        },
        {
          credits: scheduledTier?.credits,
          price: scheduledTier?.priceUsd,
        },
      )
    : "";

  const onNewCustomer = async () => {
    setLoading(true);
    const { data, err } = await Subscription.newPlan({
      priceId: selected,
    });
    setLoading(false);
    if (data && data.url) {
      window.location.href = data.url;
    } else {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 10000,
        color: "error",
        position: "top",
      });
    }
  };

  const onCreateDashboardSession = async () => {
    setLoading(true);
    const { data, err } = await Subscription.createDashboardSession();
    setLoading(false);
    if (data && data.url) {
      window.location.href = data.url;
    } else if (err) {
      return present({
        message: intl.formatMessage({ id: "payment.portalError" }),
        duration: 10000,
        color: "error",
        position: "top",
      });
    }
  };

  const onChangePlan = async () => {
    setLoading(true);
    const { data, err } = await Subscription.changePlan({
      newPriceId: selected,
      subscriptionId: subscription,
    });
    setLoading(false);
    if (data?.kind === "noop") {
      if (data?.noopReason === "already_scheduled") {
        const effectiveAt = formatLocalDate(data?.effectiveAt);
        setPendingDowngradeDate(effectiveAt);
        setPendingDowngradePriceState(
          data?.pendingDowngradePrice || selected || "",
        );
        return present({
          message: intl.formatMessage(
            { id: "payment.planNoopScheduled" },
            { date: effectiveAt || "-" },
          ),
          duration: 10000,
          color: "medium",
          position: "top",
        });
      }
      return present({
        message: intl.formatMessage({ id: "payment.planNoopCurrent" }),
        duration: 10000,
        color: "medium",
        position: "top",
      });
    }
    if (data?.kind === "downgrade_scheduled") {
      const effectiveAt = formatLocalDate(data?.effectiveAt);
      setPendingDowngradeDate(effectiveAt);
      setPendingDowngradePriceState(data?.pendingDowngradePrice || selected || "");
      return present({
        message: intl.formatMessage(
          { id: "payment.downgradeScheduled" },
          { date: effectiveAt || "-" },
        ),
        duration: 10000,
        color: "success",
        position: "top",
      });
    }
    if (data?.kind === "upgrade_now" || data) {
      setPendingDowngradeDate("");
      setPendingDowngradePriceState("");
      return present({
        message: intl.formatMessage({ id: "common.success" }),
        duration: 10000,
        color: "success",
        position: "top",
      });
    } else {
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 10000,
        color: "error",
        position: "top",
      });
    }
  };
  return isMobile ? (
    <div className="view-wrapper">
      {isLegacy && payment.active ? (
        <>
          <div className="view-pane-title mt-4">
            <FormattedMessage id="payment.title" />
          </div>

          <IonList inset={true}>
            <IonItem>
              <FormattedMessage id="payment.currentPlan" />
              <div slot="end">
                <IonNote color="medium">Enterprise</IonNote>
              </div>
            </IonItem>
            {payment.email ? (
              <IonItem>
                Email
                <div slot="end">
                  <div className="d-flex align-items-center gap-2">
                    <IonNote color="medium">{payment.email}</IonNote>
                  </div>
                </div>
              </IonItem>
            ) : null}
          </IonList>
        </>
      ) : (
        <>
          <div className="view-pane-title mt-4">
            {!payment.active ? (
              <FormattedMessage id="payment.noPlanAlert" />
            ) : (
              <>
                <FormattedMessage id="payment.manageCurrentPlan" />
              </>
            )}
          </div>
          <IonList inset={true}>
            {payment.active && currentPeriodEndDate ? (
              <IonItem>
                <IonNote color="medium">
                  <FormattedMessage
                    id="payment.currentPlanUntil"
                    values={{
                      plan: currentPlanLabel,
                      date: currentPeriodEndDate,
                    }}
                  />
                </IonNote>
              </IonItem>
            ) : null}
            {pendingDowngradeDate && scheduledPlanLabel ? (
              <IonItem>
                <IonNote color="warning">
                  <FormattedMessage
                    id="payment.scheduledPlanFrom"
                    values={{
                      plan: scheduledPlanLabel,
                      date: pendingDowngradeDate,
                    }}
                  />
                </IonNote>
              </IonItem>
            ) : null}
            <IonItem>
              <IonSelect
                label={intl.formatMessage({ id: "payment.selectAPlan" })}
                //placeholder="Make a Selection"
                onIonChange={(e) => setSelected((e.detail.value as string) || "")}
                value={selected}
              >
                {TIERS.map((t) => {
                  if (t.hide && t.priceId !== price) return null;
                  const isCurrentPlan = t.priceId === price;
                  const isScheduledPlan =
                    !isCurrentPlan &&
                    !!pendingDowngradeDate &&
                    t.priceId === effectivePendingDowngradePrice;
                  return (
                    <IonSelectOption key={t.priceId} value={t.priceId}>
                      {isCurrentPlan ? (
                        <>
                          <b>
                            <FormattedMessage id="payment.currentPlan" />
                          </b>
                          {": "}
                        </>
                      ) : null}
                      {isScheduledPlan ? (
                        <>
                          <FormattedMessage
                            id="payment.scheduledPlan"
                            values={{ date: pendingDowngradeDate }}
                          />
                          {": "}
                        </>
                      ) : null}
                      <FormattedMessage
                        id="payment.planTier"
                        defaultMessage="{credits, number} messages, {price, number, ::currency/USD}/mo"
                        values={{
                          credits: t?.credits,
                          price: t?.priceUsd,
                        }}
                      />
                    </IonSelectOption>
                  );
                })}
              </IonSelect>
            </IonItem>
            {!payment.active ? (
              selected !== "" ? (
                <IonItem button={true} onClick={onNewCustomer}>
                  {loading ? (
                    <>
                      <FormattedMessage id="common.loading" />
                      ...
                    </>
                  ) : (
                    <FormattedMessage id="payment.subscribe" />
                  )}
                </IonItem>
              ) : null
            ) : price !== selected ? (
              <IonItem button={true} onClick={onChangePlan}>
                {loading ? (
                  <>
                    <FormattedMessage id="common.loading" />
                    ...
                  </>
                ) : (
                  <FormattedMessage
                    id={changePlanCtaId}
                    defaultMessage={
                      isUpgradeSelection
                        ? "Change to this plan and pay now"
                        : "Change to this plan"
                    }
                  />
                )}
              </IonItem>
            ) : null}
          </IonList>
        </>
      )}
      {payment.active ? (
        <IonList inset={true}>
          <IonItem onClick={onCreateDashboardSession} button={true}>
            <div className="d-flex align-items-center gap-2">
              <IconCreditCardFilled size={17} />
              <FormattedMessage id="payment.managePlan" />
            </div>
          </IonItem>
        </IonList>
      ) : null}
      <PlanUsage settings={props.settings} showCta={false} isMobile={true} />
    </div>
  ) : (
    <>
      {isLegacy && payment.active ? (
        <div>
          <div className="fs-2 fw-semibold-1 mb-2">
            {" "}
            <FormattedMessage id="payment.currentPlan" />:{" "}
            <span className="fw-normal color-step-700">Enterprise</span>
          </div>
          {payment.email ? (
            <div className="fs-2 mt-2 mb-2">
              <div className="d-flex align-items-center gap-2">
                <IconMail size={17} />
                {payment.email}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div>
          {payment.active && currentPeriodEndDate ? (
            <div className="mb-2">
              <IonNote color="medium">
                <FormattedMessage
                  id="payment.currentPlanUntil"
                  values={{
                    plan: currentPlanLabel,
                    date: currentPeriodEndDate,
                  }}
                />
              </IonNote>
            </div>
          ) : null}
          {pendingDowngradeDate && scheduledPlanLabel ? (
            <div className="mb-3">
              <IonNote color="warning">
                <FormattedMessage
                  id="payment.scheduledPlanFrom"
                  values={{
                    plan: scheduledPlanLabel,
                    date: pendingDowngradeDate,
                  }}
                />
              </IonNote>
            </div>
          ) : null}
          <div>
            <h3 className="fs-3 mb-3">
              {!payment.active ? (
                <FormattedMessage id="payment.noPlanAlert" />
              ) : (
                <>
                  <FormattedMessage id="payment.manageCurrentPlan" />
                </>
              )}
            </h3>

            <Form.Select
              aria-label="Select"
              onChange={(e) => setSelected(e.target.value)}
              value={selected}
              className="max-w-40"
            >
              {selected === "" && (
                <option value={selected} disabled>
                  <FormattedMessage id="payment.selectAPlan" />
                </option>
              )}
              {TIERS.map((t) => {
                if (t.hide && t.priceId !== price) return null;
                const isCurrentPlan = t.priceId === price;
                const isScheduledPlan =
                  !isCurrentPlan &&
                  !!pendingDowngradeDate &&
                  t.priceId === effectivePendingDowngradePrice;
                return (
                  <option key={t.priceId} value={t.priceId}>
                    {isCurrentPlan ? (
                      <>
                        <b>
                          <FormattedMessage id="payment.currentPlan" />
                        </b>
                        {": "}
                      </>
                    ) : null}
                    {isScheduledPlan ? (
                      <>
                        <FormattedMessage
                          id="payment.scheduledPlan"
                          values={{ date: pendingDowngradeDate }}
                        />
                        {": "}
                      </>
                    ) : null}

                    <FormattedMessage
                      id="payment.planTier"
                      defaultMessage="{credits, number} messages, {price, number, ::currency/USD}/mo"
                      values={{
                        credits: t?.credits,
                        price: t?.priceUsd,
                      }}
                    />
                  </option>
                );
              })}
            </Form.Select>
          </div>
          <div className="mt-3 mb-3">
            {!payment.active ? (
              selected !== "" ? (
                <button
                  className="btn btn-visito-light btn-sm"
                  onClick={onNewCustomer}
                >
                  {loading ? (
                    <>
                      <FormattedMessage id="common.loading" />
                      ...
                    </>
                  ) : (
                    <FormattedMessage id="payment.subscribe" />
                  )}
                </button>
              ) : null
            ) : price !== selected ? (
              <button
                className="btn btn-visito-light btn-sm"
                onClick={onChangePlan}
              >
                {loading ? (
                  <>
                    <FormattedMessage id="common.loading" />
                    ...
                  </>
                ) : (
                  <FormattedMessage
                    id={changePlanCtaId}
                    defaultMessage={
                      isUpgradeSelection
                        ? "Change to this plan and pay now"
                        : "Change to this plan"
                    }
                  />
                )}
              </button>
            ) : null}
          </div>
        </div>
      )}
      {payment.active ? (
        <a
          className="fs-2 mt-2"
          onClick={onCreateDashboardSession}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center gap-2">
            <IconCreditCardFilled size={17} />
            <FormattedMessage id="payment.managePlan" />
          </div>
        </a>
      ) : null}
      <hr />
      <PlanUsage settings={props.settings} showCta={false} isMobile={false} />
    </>
  );
};

export default PlanManagement;
